import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import client from "@/api/client";
import { BulkToolbar, useBulkSelection } from "@/components/admin/BulkToolbar";

/**
 * AdminWaitlistManager — bulk-approve/reject/delete + inline actions.
 * Extracted out of AdminPanel.js so the toolbar and selection state
 * belong to the surface that owns them.
 */
export function AdminWaitlistManager() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/waitlist");
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const bulk = useBulkSelection("waitlist", (it) => it.entry_id, load);
  const allSelected = items.length > 0 && items.every((w) => bulk.isSelected(w.entry_id));

  const singleAction = async (id, action) => {
    try {
      if (action === "delete") {
        await client.delete(`/admin/waitlist/${id}`);
      } else {
        await client.post(`/admin/waitlist/${id}/${action}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const bulkActions = [
    { key: "approve", label: t("admin.approve"), icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
    { key: "reject", label: t("admin.reject"), icon: <XCircle className="h-3.5 w-3.5" /> },
    {
      key: "delete",
      label: t("admin.delete"),
      icon: <Trash2 className="h-3.5 w-3.5" />,
      variant: "destructive",
      requiresConfirm: true,
      confirmTitle: "Delete selected waitlist entries?",
      confirmBody: "This removes the entries permanently and cannot be undone.",
    },
  ];

  return (
    <Card className="rounded-2xl border border-border p-6 space-y-4" data-testid="admin-panel-waitlist">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">{t("admin.waitlist")}</h2>
        <Button variant="outline" size="sm" className="rounded-xl" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.tryAgain")}
        </Button>
      </div>

      <BulkToolbar {...bulk} actions={bulkActions} testId="admin-waitlist-bulk" onClear={bulk.clear} />

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="admin-waitlist-empty">{t("admin.waitlistEmpty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => bulk.toggleAll(items)}
                    aria-label={t("common.selectAll")}
                    data-testid="admin-waitlist-select-all"
                  />
                </TableHead>
                <TableHead>{t("waitlist.fullName")}</TableHead>
                <TableHead>{t("waitlist.email")}</TableHead>
                <TableHead>{t("waitlist.organization")}</TableHead>
                <TableHead>{t("waitlist.phone")}</TableHead>
                <TableHead>{t("admin.status")}</TableHead>
                <TableHead>{t("admin.receivedAt")}</TableHead>
                <TableHead className="text-end">{t("common.filter")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((w) => (
                <TableRow key={w.entry_id} data-testid="admin-waitlist-row">
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={bulk.isSelected(w.entry_id)}
                      onChange={() => bulk.toggle(w.entry_id)}
                      aria-label={`Select ${w.email}`}
                      data-testid={`admin-waitlist-select-${w.entry_id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {w.full_name}
                    {w.responses && Object.keys(w.responses).length > 0 && (
                      <details className="mt-1 text-xs">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">+{Object.keys(w.responses).length} custom fields</summary>
                        <ul className="mt-1.5 space-y-1 pl-2">
                          {Object.entries(w.responses).map(([k, v]) => (
                            <li key={k}><span className="text-muted-foreground">{k}:</span> {String(v ?? "—")}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{w.email}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{w.organization || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{w.phone || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="rounded-full capitalize">
                      {w.status === "approved" ? t("admin.approved") : w.status === "rejected" ? t("admin.rejected") : t("admin.pending")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{w.created_at ? format(parseISO(w.created_at), "MMM d, HH:mm") : ""}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      {w.status !== "approved" && (
                        <Button size="icon" variant="ghost" onClick={() => singleAction(w.entry_id, "approve")} title={t("admin.approve")} data-testid="admin-waitlist-approve">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        </Button>
                      )}
                      {w.status !== "rejected" && (
                        <Button size="icon" variant="ghost" onClick={() => singleAction(w.entry_id, "reject")} title={t("admin.reject")}>
                          <XCircle className="h-4 w-4 text-amber-600" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => singleAction(w.entry_id, "delete")} title={t("admin.delete")}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

export default AdminWaitlistManager;
